import { createApi } from '@reduxjs/toolkit/query/react';

import { fetchWithAuthBaseQuery } from '#shared/api';

import { byNumericId, isLaterSeq } from '../model/types';

import { parseDomainEventFeed } from './schemas';

import type { DomainEventFeed, DomainEventFeedArg } from '../model/types';

const paths = { events: 'api/events' } as const;

const buildEventsPath = ({ view, lookbackMs }: DomainEventFeedArg): string => {
  const params = new URLSearchParams();
  if (view) {
    params.set('view', view);
  }
  if (lookbackMs !== undefined) {
    params.set('lookbackMs', String(lookbackMs));
  }
  const query = params.toString();
  return query ? `${paths.events}?${query}` : paths.events;
};

export const domainEventsApi = createApi({
  reducerPath: 'domainEventsApi',
  baseQuery: fetchWithAuthBaseQuery,
  endpoints: builder => ({
    /**
     * The rolling event log. Live WS frames and reconnect catch-up patch it via
     * `util.updateQueryData`/`upsertQueryData`.
     *
     * ONE cache entry per relevance view (TMDXNC-54). `undefined` view keeps the
     * historical key — the unfiltered feed `/alerts-dashboard` needs.
     *
     * `lookbackMs` deliberately does NOT participate in the cache key. A wider
     * span must ACCUMULATE into the rendered entry, not mint a second one: a
     * per-span key would leave the live-frame patcher and the rendered entry
     * pointing at different objects, and re-selecting a narrower span would
     * visibly shrink the timeline. Hence serializeQueryArgs + merge +
     * forceRefetch, RTK Query's documented accumulate-into-one-entry pattern.
     *
     * Anything that patches this cache must target every cached arg rather than
     * hardcoding one — see `processes/nop-events/applyDomainEventFrame.ts`.
     */
    getDomainEvents: builder.query<DomainEventFeed, DomainEventFeedArg>({
      query: buildEventsPath,
      // Ascending by id is the cache's invariant: `merge` re-sorts a widen,
      // but RTK stores a first response as-is, and the live-frame patcher
      // binary-searches the log rather than re-sorting it.
      transformResponse: (response: unknown) => {
        const feed = parseDomainEventFeed(response);
        return { ...feed, events: [...feed.events].sort(byNumericId) };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs?.view ?? 'all'})`,
      merge: (currentCache, incoming) => {
        const seen = new Set(currentCache.events.map(event => event.id));
        for (const frame of incoming.events) {
          if (!seen.has(frame.id)) {
            seen.add(frame.id);
            currentCache.events.push(frame);
          }
        }
        currentCache.events.sort(byNumericId);

        if (isLaterSeq(incoming.latestSeq, currentCache.latestSeq)) {
          currentCache.latestSeq = incoming.latestSeq;
        }

        // Only a lookback-mode response carries a floor at all — a cursor-mode
        // (`sinceSeq`) response has none, and must change neither the floor nor
        // `capped` (PR review fix, capped-flag round). The floor itself stays
        // MINIMUM-only, same as before: a higher (shallower) incoming floor
        // never overwrites a lower, already-known one.
        //
        // `capped`, though, is no longer gated on that same floor comparison.
        // It describes whether the ACCUMULATED history currently has a
        // truncation hole — a fact that can change independently of whether
        // THIS response also happens to improve the floor:
        //   - SET whenever the incoming response reports `capped: true`,
        //     regardless of how its floor compares. A detection burst can make
        //     a wider widen's row-capped floor land HIGHER (shallower) than an
        //     already-stored, deeper floor; discarding that `capped:true`
        //     because the floor didn't also improve left the accumulated log
        //     looking genuinely uncapped at the OLD floor when a fresh
        //     truncation hole actually exists above it (TMDXNC-54 PR review).
        //   - CLEAR only when the incoming response is uncapped AND its floor
        //     is <= the stored one (equal counts) — i.e. it genuinely
        //     re-verified completeness at least as far back as what was
        //     already known. An uncapped-but-SHALLOWER response says nothing
        //     about whether the deeper stretch is complete, so it must not
        //     clear an existing truncation.
        if (incoming.historyFloorMs !== undefined) {
          if (incoming.capped) {
            currentCache.capped = true;
          } else if (
            currentCache.historyFloorMs === undefined ||
            incoming.historyFloorMs <= currentCache.historyFloorMs
          ) {
            currentCache.capped = false;
          }
          if (
            currentCache.historyFloorMs === undefined ||
            incoming.historyFloorMs < currentCache.historyFloorMs
          ) {
            currentCache.historyFloorMs = incoming.historyFloorMs;
          }
        }
        if (incoming.retentionMs !== undefined) {
          currentCache.retentionMs = incoming.retentionMs;
        }
      },
      // Only a WIDER window is worth a request: `currentArg.lookbackMs` must
      // exceed `previousArg.lookbackMs` (both default to 0 when absent) or this
      // returns false and RTK Query serves the cached entry. That is what makes
      // re-selecting the same or a narrower span issue no fetch — the inertness
      // lives entirely in this comparison, not in any caller keeping the arg
      // monotonic.
      forceRefetch: ({ currentArg, previousArg }) =>
        (currentArg?.lookbackMs ?? 0) > (previousArg?.lookbackMs ?? 0),
    }),
  }),
});

export const { useGetDomainEventsQuery } = domainEventsApi;
